import { type FC, useCallback, useState } from 'react';
import { useSelector } from 'react-redux';

import { Filters as FiltersComponent, FiltersContext } from '@/packages/Filters';
import { type TApplyFiltersSource } from '@/packages/Filters/shared/types/applyFiltersSource';
import { type TJsonQuery } from '@/packages/JsonQuery';

import { ApplyFiltersButtonContainer } from '@/microfrontends/filters/shared/containers/ApplyFiltersButton';
import { SaveSearchButtonContainer } from '@/microfrontends/filters/shared/containers/SaveSearchButtonContainer';
import { reduxActions, useReduxDispatch } from '@/microfrontends/filters/shared/store';
import { useGeoTagsSelector } from '@/microfrontends/filters/shared/store/slices/geoTags/selectors';
import { useJsonQuerySelector } from '@/microfrontends/filters/shared/store/slices/jsonQuery/selectors';
import { useLocationSelector } from '@/microfrontends/filters/shared/store/slices/location/selectors';
import {
  useIsSearchMetaLoadingSelector,
  useSearchMetaSelector,
} from '@/microfrontends/filters/shared/store/slices/searchMeta/selectors';
import { useIsUserAgentSelector } from '@/microfrontends/filters/shared/store/slices/user/selectors';
import { useVillageNameSelector } from '@/microfrontends/filters/shared/store/slices/villageName/selectors';
import { applyFilters } from '@/microfrontends/filters/shared/store/thunks/applyFilters';
import { open as openDistrictsPopup } from '@/microfrontends/filters/shared/store/thunks/districtsPopup';
import { open as openLocationPopup } from '@/microfrontends/filters/shared/store/thunks/locationPopup';
import { open as openRegionalDistrictsPopup } from '@/microfrontends/filters/shared/store/thunks/regionalDistrictsPopup';
import { updateSearchMeta } from '@/microfrontends/filters/shared/store/thunks/searchMeta';

import { selectHighways } from '../../store/slices/highways/selectors/selectHighways';
import { useApplicationContext } from '../../utils/applicationContext';
import { HighwaysModalContainer } from '../HighwaysModalContainer/HighwaysModalContainer';
import { UndergroundMapModalContainer } from '../UndergroundMapModalContainer';

import { useTooltipDisabled } from './hooks/useTooltipDisabled';
import { trackApplyClick, trackClearClick, trackFilterOpen } from './tracking';

export const FiltersContainer: FC = () => {
  const { config } = useApplicationContext();
  const dispatch = useReduxDispatch();
  const jsonQuery = useJsonQuerySelector();
  const geoTags = useGeoTagsSelector();
  const isUserAgent = useIsUserAgentSelector();
  const isTooltipAvailable = !useTooltipDisabled();
  const isSearchMetaLoading = useIsSearchMetaLoadingSelector();
  const searchMeta = useSearchMetaSelector();
  const location = useLocationSelector();
  const highways = useSelector(selectHighways);
  const villageName = useVillageNameSelector();
  const offersCount = searchMeta.data.count;
  const queryString = searchMeta.data.queryString;
  const [isUndergroundModalOpen, setIsUndergroundModalOpen] = useState(false);
  const [isHighwayModalOpen, setIsHighwayModalOpen] = useState(false);

  const handleOpenGeoPopup = useCallback(() => {
    dispatch(openLocationPopup());
  }, [dispatch]);

  const handleOpenDistrictsPopup = useCallback(() => {
    dispatch(openDistrictsPopup());
  }, [dispatch]);

  const handleOpenRegionalDistrictsPopup = useCallback(() => {
    dispatch(openRegionalDistrictsPopup());
  }, [dispatch]);

  const handleOpenUndergroundPopup = useCallback(() => {
    setIsUndergroundModalOpen(true);
  }, []);

  const handleCloseUndergroundPopup = useCallback(() => {
    setIsUndergroundModalOpen(false);
  }, []);

  const handleOpenHighwaysPopup = useCallback(() => {
    setIsHighwayModalOpen(true);
  }, []);

  const handleCloseHighwaysPopup = useCallback(() => {
    setIsHighwayModalOpen(false);
  }, []);

  const handleChange = useCallback(
    (nextJsonQuery: TJsonQuery) => {
      dispatch(reduxActions.jsonQuery.change(nextJsonQuery));
      dispatch(reduxActions.listing.resetSearchStatus());
      dispatch(updateSearchMeta());
    },
    [dispatch],
  );

  const handleApply = useCallback(async () => {
    dispatch(applyFilters());
  }, [dispatch]);

  const handleApplyClick = useCallback(
    (source: TApplyFiltersSource) => trackApplyClick({ source, queryString }),
    [queryString],
  );

  return (
    <FiltersContext
      config={config}
      currentLocation={location.data}
      geoTags={geoTags.data}
      highways={highways}
      isProfessionalSearchButtonVisible={isUserAgent}
      isTooltipAvailable={isTooltipAvailable}
      jsonQuery={jsonQuery.current}
      jsonQueryCount={offersCount}
      jsonQueryCountRefreshing={isSearchMetaLoading}
      queryString={queryString}
      villageName={villageName.data}
      onApply={handleApply}
      onApplyClick={handleApplyClick}
      onChange={handleChange}
      onClear={trackClearClick}
      onDistrictPopupOpen={handleOpenDistrictsPopup}
      onGeoPopupOpen={handleOpenGeoPopup}
      onHighwaysPopupOpen={handleOpenHighwaysPopup}
      onOpenFilter={trackFilterOpen}
      onRegionalDistrictsOpen={handleOpenRegionalDistrictsPopup}
      onUndergroundPopupOpen={handleOpenUndergroundPopup}
    >
      <FiltersComponent applyButton={<ApplyFiltersButtonContainer />} savedSearch={<SaveSearchButtonContainer />} />
      {isUndergroundModalOpen && (
        <UndergroundMapModalContainer open={isUndergroundModalOpen} onClose={handleCloseUndergroundPopup} />
      )}
      {isHighwayModalOpen && <HighwaysModalContainer open={isHighwayModalOpen} onClose={handleCloseHighwaysPopup} />}
    </FiltersContext>
  );
};
